'use client';
import { useState } from 'react';
import { fmtDate } from '../../lib/datetime';
import { useT } from '../../lib/i18n';
import { isEmail } from '../../lib/validate';
import { AuthCodeField, FieldMsg, Label, SendCodeButton, useCountdown } from '../../components/FormBits';
import { Icon } from '../../components/Icon';
import { SmartLink } from '../../components/SmartLink';
import { Working } from '../../components/Working';

/**
 * 아이디·비밀번호 찾기 — 가이드 find-account.html(상단 탭 + 인증 방식 탭).
 *
 * 서버 규격을 그대로 따른다 —
 *  · 아이디 찾기: 코드 발급(purpose=find_id) → `POST /members/find-id`(이메일+코드) → 아이디
 *  · 비밀번호 찾기: 코드 발급(purpose=find_pw) → `POST /members/find-password`(아이디+이메일+코드)
 *    → **임시 비밀번호를 메일로** 보낸다(응답에 담지 않는다 — 현행 계승).
 *    그래서 가이드의 "새 비밀번호 설정" 단계는 두지 않고, 메일 안내로 마무리한다.
 * 인증 방식 탭(이메일/휴대폰)은 가이드에 있지만 **코드 발급 키가 이메일**이라
 * 휴대폰을 골라도 이메일을 함께 받는다 — 그 사실을 안내로 밝힌다.
 */
type Tab = 'id' | 'pw';

/** 어느 탭으로 열지는 주소가 정한다 — 로그인 화면이 `아이디 찾기`·`비밀번호 찾기` 로 갈라 건다 */
export function FindFlow({ initialTab = 'id' }: { initialTab?: Tab }) {
  const t = useT();
  const [tab, setTab] = useState<Tab>(initialTab);
  return (
    <>
      <div className="fa-tab-wrap">
        <button className={`fa-tab${tab === 'id' ? ' active' : ''}`} onClick={() => setTab('id')}>{t('acc.findId')}</button>
        <button className={`fa-tab${tab === 'pw' ? ' active' : ''}`} onClick={() => setTab('pw')}>{t('acc.findPw')}</button>
      </div>
      {tab === 'id' ? <FindId onGoPw={() => setTab('pw')} /> : <FindPw />}
    </>
  );
}

function FindId({ onGoPw }: { onGoPw: () => void }) {
  const t = useT();
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [found, setFound] = useState<{ uid?: string; createdAt?: string } | null>(null);
  const { left, start } = useCountdown(180);

  const send = async () => {
    setError(null);
    if (!email.trim()) { setError(t('acc.err.emailRequired')); return; }
    if (!isEmail(email.trim())) { setError(t('acc.err.emailFormat')); return; }
    setBusy(true);
    try {
      const res = await fetch('/api/members/auth-code', {
        method: 'POST', headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ email: email.trim(), purpose: 'find_id' }),
      });
      // 서버가 이유를 말해 준다(탈퇴·정지·미가입) — 고정 문구로 덮으면 왜 안 되는지 알 수 없다
      if (!res.ok) {
        setError(((await res.json().catch(() => ({}))) as { message?: string }).message ?? t('acc.err.codeSendFailed'));
        return;
      }
      setSent(true); start();
    } finally { setBusy(false); }
  };

  const submit = async () => {
    setError(null);
    if (code.length !== 6) { setError(t('acc.err.codeRequired')); return; }
    setBusy(true);
    try {
      const res = await fetch('/api/members/find-id', {
        method: 'POST', headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ email: email.trim(), authCode: code }),
      });
      const body = (await res.json().catch(() => ({}))) as { uid?: string; createdAt?: string; message?: string };
      if (!res.ok) { setError(body.message ?? t('acc.err.codeInvalid')); return; }
      setFound(body);
    } finally { setBusy(false); }
  };

  if (found) return (
    <div className="form-box">
      <div className="fa-result">
        <div className="fa-result-icon success"><Icon name="circle-check" size={40} /></div>
        <p className="fa-result-label">{t('acc.foundIdLabel')}</p>
        <p className="fa-result-value">{found.uid}</p>
        {found.createdAt && <p className="fa-result-desc">{t('acc.joinedAt')} {fmtDate(found.createdAt)}</p>}
        <div className="fa-result-btns m_20">
          <SmartLink href="/login" className="action-btn action-submit" style={{ justifyContent: 'center' }}>{t('acc.login')}</SmartLink>
          <button className="action-btn action-cancel" style={{ justifyContent: 'center' }} onClick={onGoPw}>{t('acc.findPw')}</button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="form-box">
      <div className="form-wrap">
        <div className="form-field">
          <Label required>{t('acc.email')}</Label>
          <div className="form-input-group">
            <input type="email" className="form-input" value={email} placeholder={t('acc.emailJoinedPh')}
              onChange={(e) => { setEmail(e.target.value); setSent(false); }} />
            {!sent && <SendCodeButton onSend={() => void send()} sending={busy} />}
          </div>
        </div>
        {sent && (
          <AuthCodeField label={t('acc.code')} code={code} onChange={setCode}
            seconds={left} onSend={() => void send()} sending={busy} />
        )}
        <FieldMsg error={error} />
        <button className="login-submit-btn" disabled={busy || !sent} onClick={() => void submit()}>
          {busy && <Working inline />}{t('acc.findId')}
        </button>
      </div>
    </div>
  );
}

function FindPw() {
  const t = useT();
  const [uid, setUid] = useState('');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const { left, start } = useCountdown(180);

  const send = async () => {
    setError(null);
    if (!email.trim()) { setError(t('acc.err.emailRequired')); return; }
    if (!isEmail(email.trim())) { setError(t('acc.err.emailFormat')); return; }
    setBusy(true);
    try {
      const res = await fetch('/api/members/auth-code', {
        method: 'POST', headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ email: email.trim(), purpose: 'find_pw' }),
      });
      // 서버가 이유를 말해 준다(탈퇴·정지·미가입) — 고정 문구로 덮으면 왜 안 되는지 알 수 없다
      if (!res.ok) {
        setError(((await res.json().catch(() => ({}))) as { message?: string }).message ?? t('acc.err.codeSendFailed'));
        return;
      }
      setSent(true); start();
    } finally { setBusy(false); }
  };

  const submit = async () => {
    setError(null);
    if (!uid.trim()) { setError(t('acc.err.uidRequired')); return; }
    if (code.length !== 6) { setError(t('acc.err.codeRequired')); return; }
    setBusy(true);
    try {
      const res = await fetch('/api/members/find-password', {
        method: 'POST', headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ uid: uid.trim(), email: email.trim(), authCode: code }),
      });
      const body = (await res.json().catch(() => ({}))) as { message?: string };
      if (!res.ok) { setError(body.message ?? t('acc.err.codeInvalid')); return; }
      setDone(true);
    } finally { setBusy(false); }
  };

  if (done) return (
    <div className="form-box">
      <div className="fa-result">
        <div className="fa-result-icon success"><Icon name="shield-check" size={40} /></div>
        <p className="fa-result-label">{t('acc.tempPwSent')}</p>
        <p className="fa-result-desc">{t('acc.tempPwDesc')}</p>
        <div className="fa-result-btns">
          <SmartLink href="/login" className="action-btn action-submit" style={{ justifyContent: 'center' }}>{t('acc.login')}</SmartLink>
        </div>
      </div>
    </div>
  );

  return (
    <div className="form-box">
      <div className="form-wrap">
        <div className="form-field">
          <Label required>{t('acc.uid')}</Label>
          <input type="text" className="form-input" value={uid} placeholder={t('acc.uidJoinedPh')}
            onChange={(e) => setUid(e.target.value)} />
        </div>
        <div className="form-field">
          <Label required>{t('acc.email')}</Label>
          <div className="form-input-group">
            <input type="email" className="form-input" value={email} placeholder={t('acc.emailJoinedPh')}
              onChange={(e) => { setEmail(e.target.value); setSent(false); }} />
            {!sent && <SendCodeButton onSend={() => void send()} sending={busy} />}
          </div>
        </div>
        {sent && (
          <AuthCodeField label={t('acc.code')} code={code} onChange={setCode}
            seconds={left} onSend={() => void send()} sending={busy} />
        )}
        <FieldMsg error={error} hint={t('acc.tempPwHint')} />
        <button className="login-submit-btn" disabled={busy || !sent} onClick={() => void submit()}>
          {busy && <Working inline />}{t('acc.issueTempPw')}
        </button>
      </div>
    </div>
  );
}
